/**
 * Настраиваемые списки (АН-09, АН-10): выбор источника и страница списка с видами, отбором, группировкой,
 * итогами и выгрузкой. Доступен любому сотруднику: в списке только то, на что у него есть право.
 */
import type { SourceInfo } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { plural } from '../../lib/format';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ExportButtons } from './ExportButtons';
import { ListViewBar } from './ListViewBar';
import { ResultView } from './ResultView';
import { sourcesQuery, useListView } from './useListView';

export function ListsIndexPage() {
  useCrumbs([{ label: 'Списки' }]);
  const q = useQuery(sourcesQuery);
  const groups = useMemo(() => {
    const m = new Map<string, SourceInfo[]>();
    for (const s of q.data ?? []) m.set(s.group, [...(m.get(s.group) ?? []), s]);
    return [...m.entries()];
  }, [q.data]);
  if (q.isPending) return <Spinner />;
  if (q.isError)
    return (
      <Page>
        <PageHeader title="Списки" />
        <ErrorBox error={q.error} />
      </Page>
    );
  return (
    <Page>
      <PageHeader
        title="Списки"
        subtitle="Любой список портала можно настроить под себя: колонки, отбор, группировку и итоги, сохранить вид и выгрузить в Excel или PDF."
      />
      {groups.length ? (
        <div className="flex flex-col gap-6">
          {groups.map(([group, items]) => (
            <Panel key={group} title={group} bodyClassName="p-0">
              <ul className="divide-y divide-line">
                {items.map((s) => (
                  <li key={s.key}>
                    <Link
                      to="/reports/lists/$source"
                      params={{ source: s.key }}
                      className="flex min-h-11 flex-col gap-0.5 px-4 py-3 hover:bg-tile/60"
                    >
                      <span className="font-medium">{s.title}</span>
                      <span className="text-[13px] text-ink-2">
                        {s.description} · {s.fields.length} {plural(s.fields.length, 'поле', 'поля', 'полей')}
                        {s.moneyHidden ? ' · суммы скрыты уровнем доступа' : ''}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          ))}
        </div>
      ) : (
        <EmptyState title="Нет доступных списков">
          Списки появляются по вашим правам: например, «Видеть договоры» открывает список договоров.
        </EmptyState>
      )}
    </Page>
  );
}

export function SourceListPage() {
  const { source } = useParams({ strict: false }) as { source: string };
  const q = useQuery(sourcesQuery);
  const src = q.data?.find((s) => s.key === source);
  useCrumbs(src ? [{ label: 'Списки', to: '/reports/lists' }, { label: src.title }] : null, q.error);
  const lv = useListView(src);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} className="m-4" />;
  if (!src)
    return (
      <Page>
        <PageHeader title="Список не найден" />
        <EmptyState title="Нет такого списка или нет права на него">
          <Link
            to="/reports/lists"
            className="inline-flex min-h-11 items-center text-accent-text underline-offset-4 hover:underline"
          >
            Все списки
          </Link>
        </EmptyState>
      </Page>
    );
  return (
    <Page>
      <PageHeader
        title={src.title}
        subtitle={src.description}
        actions={
          lv.cleaned ? (
            <ExportButtons source={src.key} config={lv.cleaned} asOf={lv.asOf} title={src.title} />
          ) : null
        }
      />
      {src.moneyHidden ? (
        <p className="mb-4 rounded-lg border border-line bg-surface px-5 py-3 text-[14px] text-ink-2">
          Поля с суммами скрыты: их открывает уровень доступа к деньгам. Их нет ни в списке, ни в отборе, ни в
          выгрузке.
        </p>
      ) : null}
      <Panel bodyClassName="p-0">
        <ListViewBar
          lv={lv}
          actions={
            src.dateField ? (
              <div className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-2">
                На дату
                <Input
                  className="h-11 w-40 md:h-8"
                  type="date"
                  value={lv.asOf}
                  onChange={(e) => lv.setAsOf(e.target.value)}
                  aria-label="Дата среза"
                />
              </div>
            ) : null
          }
        />
        {lv.error ? (
          <ErrorBox error={lv.error} className="m-4" />
        ) : lv.isLoading || !lv.list || !lv.cleaned ? (
          <Spinner />
        ) : (
          <>
            {lv.list.asOfNote ? (
              <p className="border-b border-line px-4 py-2 text-[12px] text-ink-2">{lv.list.asOfNote}</p>
            ) : null}
            <ResultView list={lv.list} config={lv.cleaned} rows={lv.rows} fields={src.fields} />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 text-[13px] text-ink-2">
              <span>
                Показано {lv.list.groups ? lv.list.total : lv.rows.length} из {lv.list.total}
                {lv.list.truncated ? ' (источник прочитан не весь: уточните отбор)' : ''}
              </span>
              {lv.hasNext ? (
                <button
                  type="button"
                  disabled={lv.fetchingNext}
                  onClick={lv.fetchNext}
                  className="inline-flex min-h-11 items-center rounded-md border border-line-strong px-4 text-[14px] text-ink hover:bg-tile disabled:opacity-50"
                >
                  {lv.fetchingNext ? 'Загрузка…' : 'Показать ещё'}
                </button>
              ) : null}
            </div>
          </>
        )}
      </Panel>
    </Page>
  );
}
